import { Checkbox, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Radio, Select, SelectChangeEvent, TextField, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { FLOOR_MIN_WINDOW_MINUTES, FLOOR_RECOVERY_DEADBAND_PERCENT, FLOOR_WINDOW_OPTIONS, formatMinutes, formatWindow, QUOTA_DIRECTION_CEILING, QUOTA_DIRECTION_FLOOR, QuotaDirection } from '../types';
import { DataWithUnitInput } from '../DataWithUnitInput';
import './index.css';

interface RuleTypeSelectorProps {
    value: QuotaDirection;
    // a rule's type is fixed once created (P2)
    locked: boolean;
    onChange: (direction: QuotaDirection) => void;
}

const RULE_TYPES: { direction: QuotaDirection, title: string, description: string }[] = [
    {
        direction: QUOTA_DIRECTION_CEILING,
        title: '▲ Maximum usage',
        description: 'Act as usage climbs towards a limit over the quota period.',
    },
    {
        direction: QUOTA_DIRECTION_FLOOR,
        title: '▼ Minimum usage',
        description: 'Act when a WAN link carries less than expected in a rolling window.',
    },
];

export function RuleTypeSelector(props: RuleTypeSelectorProps) {
    const { value, locked, onChange } = props;
    return (
        <Grid container spacing={2}>
            {RULE_TYPES.map(rule => {
                const selected = rule.direction === value;
                const disabled = locked && !selected;
                return (
                    <Grid item xs={12} md={6} key={rule.direction}>
                        <div
                            className={`floor_quota--rule_card ${selected ? 'floor_quota--rule_card--selected' : ''} ${disabled ? 'floor_quota--rule_card--disabled' : ''}`}
                            onClick={() => { if (!locked) onChange(rule.direction) }}
                        >
                            <Radio size='small' checked={selected} disabled={disabled} />
                            <div>
                                <div className='floor_quota--rule_card--title'>{rule.title}</div>
                                <div className='floor_quota--rule_card--description'>{rule.description}</div>
                            </div>
                        </div>
                    </Grid>
                );
            })}
            {locked && <Grid item xs={12}>
                <FormHelperText>A rule's type can't be changed after it is created. Delete it and add a new one instead.</FormHelperText>
            </Grid>}
        </Grid>
    );
}

interface FloorConfigSectionProps {
    period: number;
    periodUnit: string;
    limit: number;
    recoveryWindows: number;
    warmupWindows: number;
    disabled?: boolean;
    onChangeWindow: (period: number, unit: string) => void;
    onChangeLimit: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onChangeRecoveryWindows: (windows: number) => void;
    onChangeWarmupWindows: (windows: number) => void;
}

// a selection of the suppressions the engine always applies, listed so operators know they exist
const ALWAYS_SKIPPED = [
    'Device is not reporting telemetry',
    'Interface is disabled, or switched off by another quota rule',
];

const windowKey = (period: number, unit: string) => `${period}${unit}`;

export function FloorConfigSection(props: FloorConfigSectionProps) {
    const { period, periodUnit, limit, recoveryWindows, warmupWindows, disabled, onChangeWindow, onChangeLimit, onChangeRecoveryWindows, onChangeWarmupWindows } = props;

    // a rule saved before the 15 minute minimum keeps its window visible, disabled, so the
    // operator sees why Save is off rather than a blank select
    const windowAllowed = FLOOR_WINDOW_OPTIONS.some(o => o.period === period && o.unit === periodUnit);
    const windowMinutes = windowAllowed
        ? period * (periodUnit === 'H' ? 60 : 1)
        : 0;
    const mbps = windowMinutes > 0 ? (limit * 8) / (windowMinutes * 60) / 1000000 : 0;

    const handleWindowChange = (event: SelectChangeEvent) => {
        const option = FLOOR_WINDOW_OPTIONS.find(o => windowKey(o.period, o.unit) === event.target.value);
        if (option) onChangeWindow(option.period, option.unit);
    }

    const handleWindowsInput = (onChange: (windows: number) => void, min: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(event.target.value, 10);
        onChange(isNaN(value) ? min : Math.max(min, value));
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
                <FormControl fullWidth size='small'>
                    <InputLabel id='floor-window-label'>Evaluation Window</InputLabel>
                    <Select labelId='floor-window-label' label='Evaluation Window' value={windowKey(period, periodUnit)} onChange={handleWindowChange} disabled={disabled}>
                        {!windowAllowed && period > 0 && <MenuItem value={windowKey(period, periodUnit)} disabled>{formatWindow(period, periodUnit)} (no longer allowed)</MenuItem>}
                        {FLOOR_WINDOW_OPTIONS.map(o => <MenuItem key={windowKey(o.period, o.unit)} value={windowKey(o.period, o.unit)}>{formatWindow(o.period, o.unit)} (rolling)</MenuItem>)}
                    </Select>
                    {windowAllowed || period <= 0
                        ? <FormHelperText>Checked every 5 minutes over the trailing window.</FormHelperText>
                        : <FormHelperText error>Pick a window of {FLOOR_MIN_WINDOW_MINUTES} minutes or more. WAN usage takes about 11 minutes to arrive, so a shorter window is mostly waiting.</FormHelperText>}
                </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
                <DataWithUnitInput value={limit} onChange={onChangeLimit} disabled={disabled} label='Minimum per window' />
                {mbps > 0 && <FormHelperText>≈ {mbps < 1 ? mbps.toFixed(2) : mbps.toFixed(1)} Mbps sustained average</FormHelperText>}
            </Grid>
            <Grid item xs={12} md={4}>
                <TextField fullWidth size='small' label='Quota Start' value='Rolling window - no start date or renewal' disabled />
            </Grid>
            <Grid item xs={12} md={6}>
                <div className='floor_quota--panel'>
                    <Typography className='floor_quota--panel--title'>Recovery</Typography>
                    <div className='floor_quota--inline'>
                        Back to normal after
                        <TextField size='small' type='number' value={recoveryWindows} onChange={handleWindowsInput(onChangeRecoveryWindows, 1)} disabled={disabled} inputProps={{ min: 1, className: 'floor_quota--number' }} />
                        windows in a row at or above {FLOOR_RECOVERY_DEADBAND_PERCENT}% of the minimum
                    </div>
                    <FormHelperText>
                        {windowMinutes > 0 && `= ${formatMinutes(recoveryWindows * windowMinutes)}. `}
                        The extra {FLOOR_RECOVERY_DEADBAND_PERCENT - 100}% stops a link sitting on the line from flapping.
                    </FormHelperText>
                    <Typography className='floor_quota--panel--title floor_quota--panel--title--spaced'>Warm-up</Typography>
                    <div className='floor_quota--inline'>
                        Skip the first
                        <TextField size='small' type='number' value={warmupWindows} onChange={handleWindowsInput(onChangeWarmupWindows, 0)} disabled={disabled} inputProps={{ min: 0, className: 'floor_quota--number' }} />
                        window(s)
                    </div>
                    <FormHelperText>after the rule is created, the device boots, or an interface is re-enabled.</FormHelperText>
                </div>
            </Grid>
            <Grid item xs={12} md={6}>
                <div className='floor_quota--panel'>
                    <Typography className='floor_quota--panel--title'>Don't evaluate when…</Typography>
                    {ALWAYS_SKIPPED.map(text => <div key={text} className='floor_quota--locked_row'>
                        <Checkbox size='small' checked disabled />{text}<LockOutlinedIcon className='floor_quota--lock' />
                    </div>)}
                    <FormHelperText>Skipped windows show as “Not Evaluated” and reset the count of windows in a row.</FormHelperText>
                </div>
            </Grid>
        </Grid>
    );
}
